import { useState } from "react";
import { format } from "date-fns";
import { Loader2, X, Calendar as CalIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import apiClient from "./api";
import { SERVICES } from "../data/mock";

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
  "5:00 PM", "6:00 PM", "7:00 PM",
];

export default function ConvertReminderModal({ reminder, open, onClose, onConverted }) {
  const [date, setDate] = useState();
  const [time_slot, setTimeSlot] = useState("");
  const [service, setService] = useState(reminder?.service || "");
  const [pet_name, setPetName] = useState(reminder?.pet_name || "");
  const [pet_breed, setPetBreed] = useState(reminder?.pet_breed || "");
  const [pet_type, setPetType] = useState("Dog");
  const [location, setLocation] = useState("Studio");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open || !reminder) return null;

  const submit = async () => {
    if (!date || !time_slot || !service) {
      toast.error("Date, time slot and service are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(
        `/api/admin/reminders/${reminder.id}/convert`,
        {
          date: format(date, "yyyy-MM-dd"),
          time_slot,
          service,
          location,
          pet_name: pet_name || undefined,
          pet_breed: pet_breed || undefined,
          pet_type: pet_type || "Dog",
          message: message || undefined,
          booking_status: "confirmed",
        }
      );
      toast.success(`Booking confirmed for ${reminder.customer_name}`);
      onConverted && onConverted(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="convert-modal"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-500/15 border border-emerald-500/40 w-10 h-10 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-300">Confirm booking</p>
              <h2 className="font-heading text-lg font-bold text-white truncate max-w-[220px]">
                {reminder.customer_name}
              </h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="convert-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <p className="text-white/60 text-xs">
            Customer <span className="text-white/90">{reminder.customer_name}</span>
            {reminder.customer_phone && <> · <span className="text-[#C9BCFF]">{reminder.customer_phone}</span></>}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Service *</label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger data-testid="convert-service" className={inputCls + " text-left"}>
                  <SelectValue placeholder="Pick a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-60 z-[90]">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="convert-date-picker"
                    className={`${inputCls} text-left flex items-center justify-between`}
                  >
                    <span className={date ? "text-white" : "text-white/40"}>
                      {date ? format(date, "PPP") : "Pick a date"}
                    </span>
                    <CalIcon className="w-4 h-4 text-[#C9BCFF]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10 z-[90]" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Time slot *</label>
              <Select value={time_slot} onValueChange={setTimeSlot}>
                <SelectTrigger data-testid="convert-time-slot" className={inputCls + " text-left"}>
                  <SelectValue placeholder="Pick a time" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-60 z-[90]">
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Location</label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger data-testid="convert-location" className={inputCls + " text-left"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white z-[90]">
                  <SelectItem value="Studio">At Studio</SelectItem>
                  <SelectItem value="Home">Home (Doorstep)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Pet type</label>
              <Select value={pet_type} onValueChange={setPetType}>
                <SelectTrigger data-testid="convert-pet-type" className={inputCls + " text-left"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white z-[90]">
                  <SelectItem value="Dog">Dog</SelectItem>
                  <SelectItem value="Cat">Cat</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Pet name</label>
              <input
                data-testid="convert-pet-name"
                value={pet_name}
                onChange={(e) => setPetName(e.target.value)}
                className={inputCls}
                placeholder="e.g. Coco"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Breed</label>
              <input
                data-testid="convert-pet-breed"
                value={pet_breed}
                onChange={(e) => setPetBreed(e.target.value)}
                className={inputCls}
                placeholder="e.g. Labrador"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Notes</label>
              <textarea
                data-testid="convert-notes"
                rows={2}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Any special instructions"
                className={inputCls + " resize-none"}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="convert-submit"
              className="btn-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : (
                <><CheckCircle2 className="w-4 h-4" /> Confirm booking</>
              )}
            </button>
            <button
              onClick={onClose}
              className="btn-secondary !py-2.5 !px-4 !text-sm sm:!flex-none"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
